import { PDF_DOCUMENT_CONSTANTS } from '../components/workspace/pdf/constants/pdfDocumentConstants';
import { httpGET } from '../services/http';
import type { Light } from '../types/light';
import { fetchCatalogueProduct } from './catalogueProductCache';
import type { LuminairePdfDisplay } from './pdfLuminaireDisplayFormat';

export type { LuminairePdfDisplay } from './pdfLuminaireDisplayFormat';

function buildCatalogueDisplayName(productName?: string, variantName?: string): string {
  if (productName && variantName) return `${productName} · ${variantName}`;
  return variantName ?? productName ?? 'Unknown product';
}

async function fetchManufacturersMap(): Promise<Map<number, string>> {
  try {
    const { data } = await httpGET('/catalogue/manufacturers');
    if (!Array.isArray(data)) return new Map();
    return new Map(data.map((m: { id: string | number; name: string }) => [Number(m.id), m.name]));
  } catch {
    return new Map();
  }
}

/** Resolve PDF-facing luminaire labels (catalogue names when assigned, Taman names otherwise). */
export async function resolveLuminairePdfDisplayByUuid(lights: Light[]): Promise<Record<string, LuminairePdfDisplay>> {
  if (lights.length === 0) return {};

  const manufacturers = await fetchManufacturersMap();
  const productCache = new Map<number, Awaited<ReturnType<typeof fetchCatalogueProduct>>>();
  const result: Record<string, LuminairePdfDisplay> = {};

  await Promise.all(
    lights.map(async (light) => {
      const tamanName = light.name;

      if (light.productId == null || light.variantId == null) {
        result[light.uuid] = {
          manufacturer: PDF_DOCUMENT_CONSTANTS.LAMP.MANUFACTURER,
          displayName: tamanName,
          lumenOutput: light.lumenOutput,
        };
        return;
      }

      let productInfo = productCache.get(light.productId);
      if (productInfo === undefined) {
        productInfo = await fetchCatalogueProduct(light.productId);
        productCache.set(light.productId, productInfo);
      }

      const variant = productInfo?.variants?.find((v) => Number(v.id) === light.variantId);
      const variantName = variant?.name;
      const displayName = buildCatalogueDisplayName(productInfo?.name, variantName);

      const manufacturerId = light.manufacturerId ?? productInfo?.manufacturerId;
      const manufacturer =
        (manufacturerId != null ? manufacturers.get(manufacturerId) : undefined) ??
        PDF_DOCUMENT_CONSTANTS.LAMP.MANUFACTURER;

      result[light.uuid] = {
        manufacturer,
        displayName,
        tamanName,
        lumenOutput: variant?.lumenOutput ?? light.lumenOutput,
      };
    }),
  );

  return result;
}
